import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, Loader2 } from "lucide-react";
import { useShop } from "../context/ShopContext";
import { api } from "../lib/api";

export default function CheckoutModal({ open, onClose }) {
  const { cart, preorder } = useShop();
  const [form, setForm] = useState({ name: "", email: "", phone: "", marketing_consent: false });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [serverError, setServerError] = useState("");

  const validate = () => {
    const e = {};
    if (form.name.trim().length < 2) e.name = "Enter your full name";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Enter a valid email";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    setServerError("");
    if (!validate()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/checkout/session", {
        items: cart.map((it) => ({ slug: it.slug, quantity: it.qty, variant: it.variant })),
        customer: { name: form.name.trim(), email: form.email.trim(), phone: form.phone.trim() || null, marketing_consent: form.marketing_consent },
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (err) {
      setServerError(err.response?.data?.detail || "Checkout failed. Please try again.");
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          data-testid="checkout-modal"
          className="fixed inset-0 z-[78] flex items-end justify-center bg-black/80 sm:items-center"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={onClose}
        >
          <motion.div
            className="w-full max-w-md border border-ll-line bg-ll-coal p-6"
            initial={{ y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 40, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.76, 0, 0.24, 1] }}
            onClick={(e) => e.stopPropagation()}
            role="dialog" aria-label="Guest checkout details"
          >
            <div className="flex items-center justify-between">
              <p className="font-display text-2xl tracking-[0.1em]">GUEST CHECKOUT</p>
              <button data-testid="checkout-modal-close" onClick={onClose} aria-label="Close checkout"><X className="h-5 w-5" /></button>
            </div>
            <p className="mt-1 font-tech text-[11px] tracking-[0.15em] text-ll-steel">SECURE PAYMENT VIA STRIPE — NO ACCOUNT NEEDED</p>

            <form onSubmit={submit} className="mt-6 space-y-4" noValidate>
              <div>
                <label htmlFor="co-name" className="font-tech text-[11px] tracking-[0.2em] text-ll-steel">FULL NAME</label>
                <input id="co-name" data-testid="checkout-name-input" type="text" value={form.name} autoComplete="name"
                       onChange={(e) => setForm({ ...form, name: e.target.value })}
                       className="mt-1 w-full border border-ll-linestrong bg-ll-black px-3 py-3 text-sm outline-none transition-colors duration-200 focus:border-ll-red" />
                {errors.name && <p data-testid="checkout-name-error" className="mt-1 text-xs text-ll-red">{errors.name}</p>}
              </div>
              <div>
                <label htmlFor="co-email" className="font-tech text-[11px] tracking-[0.2em] text-ll-steel">EMAIL</label>
                <input id="co-email" data-testid="checkout-email-input" type="email" value={form.email} autoComplete="email"
                       onChange={(e) => setForm({ ...form, email: e.target.value })}
                       className="mt-1 w-full border border-ll-linestrong bg-ll-black px-3 py-3 text-sm outline-none transition-colors duration-200 focus:border-ll-red" />
                {errors.email && <p data-testid="checkout-email-error" className="mt-1 text-xs text-ll-red">{errors.email}</p>}
              </div>
              <div>
                <label htmlFor="co-phone" className="font-tech text-[11px] tracking-[0.2em] text-ll-steel">PHONE (OPTIONAL)</label>
                <input id="co-phone" data-testid="checkout-phone-input" type="tel" value={form.phone} autoComplete="tel"
                       onChange={(e) => setForm({ ...form, phone: e.target.value })}
                       className="mt-1 w-full border border-ll-linestrong bg-ll-black px-3 py-3 text-sm outline-none transition-colors duration-200 focus:border-ll-red" />
              </div>
              <label className="flex cursor-pointer items-start gap-3">
                <input data-testid="checkout-marketing-consent" type="checkbox" checked={form.marketing_consent}
                       onChange={(e) => setForm({ ...form, marketing_consent: e.target.checked })}
                       className="mt-0.5 h-4 w-4 accent-ll-red" />
                <span className="text-xs text-ll-steel">Email me about future drops and restocks. Optional — not required to order.</span>
              </label>

              {serverError && <p data-testid="checkout-server-error" className="border border-ll-red/50 bg-ll-red/10 px-3 py-2 text-xs text-ll-red">{serverError}</p>}

              <motion.button
                data-testid="checkout-pay-button"
                whileTap={{ scale: 0.98 }}
                type="submit" disabled={loading}
                className="flex w-full items-center justify-center gap-2 bg-ll-red py-4 font-tech text-sm tracking-[0.25em] transition-colors duration-200 hover:bg-ll-redhover disabled:opacity-60"
              >
                {loading && <Loader2 className="h-4 w-4 animate-spin" />}
                {loading ? "REDIRECTING…" : "CONTINUE TO PAYMENT"}
              </motion.button>
              <p className="text-center text-[11px] text-ll-steel">
                Shipping address and payment are collected on Stripe's secure page. Estimated shipping shown on the next step.
              </p>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
